import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { createVoucher, CreateVoucherRequest } from "../../../../services/merchant/merchant_actions";
import { purple } from "../../../../theme/colors";

interface CreateVoucherDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateVoucherDialog({
  open,
  onClose,
  onSuccess,
}: CreateVoucherDialogProps) {
  const [amount, setAmount] = useState<number>(0);
  const [numberOfVouchers, setNumberOfVouchers] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useFeedback();

  const handleSubmit = async () => {
    if (amount <= 0) {
      showSnackbar("Amount must be greater than 0");
      return;
    }
    if (numberOfVouchers <= 0) {
      showSnackbar("Number of vouchers must be greater than 0");
      return;
    }

    setIsLoading(true);
    try {
      const request: CreateVoucherRequest = {
        amount,
        count: numberOfVouchers,
      };
      
      await createVoucher(request);
      showSnackbar(`${numberOfVouchers} voucher(s) created successfully!`);
      onSuccess();
      onClose();
      // Reset form
      setAmount(0);
      setNumberOfVouchers(1);
    } catch (error) {
      showSnackbar("Failed to create vouchers");
      console.error("Error creating vouchers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form
      setAmount(0);
      setNumberOfVouchers(1);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="600">
          Create Vouchers
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                placeholder="Enter voucher amount"
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Number of Vouchers"
                type="number"
                value={numberOfVouchers}
                onChange={(e) => setNumberOfVouchers(Number(e.target.value))}
                placeholder="Enter number of vouchers to create"
                disabled={isLoading}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || amount <= 0 || numberOfVouchers <= 0}
          sx={{ backgroundColor: purple }}
        >
          {isLoading ? "Creating..." : "Create Vouchers"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
